import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import { createQColumnHelper } from '@qualio/ui-components';
import { ListQualityEvent } from '../../types';
import { renderDueColumn, renderResolvedColumn } from './util';

const columnHelper = createQColumnHelper<ListQualityEvent>();

type LocaliseDate = (utcDate: string | Date) => Date;

export type EventTableColumnsOptions = {
  t: (key: string, options?: Record<string, string>) => string;
  localiseDate: LocaliseDate;
  statusValueLabelMap: Record<string, string>;
  disableTextColumnSort: boolean;
  isConfigurableColumnsEnabled: boolean;
  isCollaboratorsColumnEnabled: boolean;
};

/** Every event table column id, in the order the table renders them. */
export const EVENT_TABLE_COLUMN_IDS = [
  'code',
  'title',
  'risk',
  'product',
  'tags',
  'owner',
  'due_at',
  'resolved_at',
  'steps',
  'status',
  'type',
  'description',
  'created_at',
  'updated_at',
  'root_cause',
  'owner_email',
  'severity',
  'probability',
  'collaborators',
] as const;

/** Which columns a fresh browser renders: `true` is shown, `false` is hidden until the user picks it. */
export const EVENT_TABLE_INITIAL_VISIBILITY: Record<string, boolean> = {
  code: true,
  title: true,
  risk: true,
  product: true,
  tags: true,
  owner: true,
  due_at: true,
  resolved_at: true,
  steps: true,
  status: true,
  type: false,
  description: false,
  created_at: false,
  updated_at: false,
  root_cause: false,
  owner_email: false,
  severity: false,
  probability: false,
  collaborators: true,
};

const toLocalDate = (seconds: number | null | undefined, localiseDate: LocaliseDate) =>
  seconds ? localiseDate(DateTime.fromSeconds(seconds).toString()) : undefined;

export const getEventTableColumns = ({
  t,
  localiseDate,
  statusValueLabelMap,
  disableTextColumnSort,
  isConfigurableColumnsEnabled,
  isCollaboratorsColumnEnabled,
}: EventTableColumnsOptions) => {
  const baseColumns = [
    columnHelper.id((event) => event.code, {
      header: t('common.labels.id'),
      id: 'code',
      enableSorting: true,
      minWidth: '100px',
      maxWidth: '250px',
    }),
    {
      id: 'title',
      accessorKey: 'title',
      header: t('common.labels.title'),
      enableSorting: !disableTextColumnSort,
      cell: ({ row: { original: event } }: { row: { original: ListQualityEvent } }) => {
        return <Link to={`events/${event.id}`}>{event.title}</Link>;
      },
      meta: {
        minWidth: '200px',
      },
    },
    columnHelper.text((event) => event.risk, {
      header: t('common.labels.risk'),
      id: 'risk',
      enableSorting: true,
      minWidth: '115px',
      maxWidth: '150px',
    }),
    columnHelper.tag((event) => event.products?.map((product) => product.name) || [], {
      header: t('common.labels.product'),
      id: 'product',
      enableSorting: false,
      minWidth: '100px',
      statuses: {},
    }),
    columnHelper.tag((event) => (event.tags ? event.tags.split(',') : []), {
      header: t('common.labels.tags'),
      id: 'tags',
      enableSorting: false,
      statuses: {},
      minWidth: '100px',
    }),
    columnHelper.text((event) => event.owner, {
      header: t('common.labels.owner'),
      id: 'owner',
      enableSorting: true,
      minWidth: '100px',
      maxWidth: '250px',
    }),
    columnHelper.text(
      (event) =>
        renderDueColumn({
          resolvedAt: event.resolved_at,
          status: event.status,
          dueDate: event.due_date,
          type: 'event',
        }),
      {
        header: t('common.labels.due'),
        id: 'due_at',
        enableSorting: true,
        minWidth: '100px',
        maxWidth: '150px',
      },
    ),
    columnHelper.date((event) => renderResolvedColumn(event, localiseDate), {
      header: t('common.labels.resolved'),
      id: 'resolved_at',
      enableSorting: true,
      minWidth: '100px',
      maxWidth: '150px',
    }),
    columnHelper.text((event) => `${event.steps_closed}/${event.steps_total}`, {
      header: t('common.labels.steps'),
      id: 'steps',
      minWidth: '70px',
      maxWidth: '150px',
      enableSorting: false,
    }),
    columnHelper.status((event) => statusValueLabelMap[event.status] as string, {
      header: t('common.labels.status'),
      id: 'status',
      enableSorting: true,
      statuses: {
        [t('common.labels.open')]: 'blue',
        [t('common.labels.closed')]: 'green',
        [t('common.labels.imported')]: 'yellow',
        [t('common.statuses.rejected')]: 'red',
        [t('common.statuses.canceled')]: 'red',
      },
      minWidth: '100px',
      maxWidth: '150px',
    }),
  ];

  const configurableColumns = [
    columnHelper.text((event) => event.workflow, {
      header: t('common.labels.type'),
      id: 'type',
      enableSorting: false,
      minWidth: '100px',
      maxWidth: '250px',
    }),
    columnHelper.text((event) => event.description, {
      header: t('common.labels.description'),
      id: 'description',
      enableSorting: false,
      minWidth: '150px',
      maxWidth: '300px',
    }),
    columnHelper.date((event) => toLocalDate(event.created_at, localiseDate), {
      header: t('common.labels.created'),
      id: 'created_at',
      enableSorting: true,
      minWidth: '100px',
      maxWidth: '150px',
    }),
    columnHelper.date((event) => toLocalDate(event.updated_at, localiseDate), {
      header: t('common.labels.lastModified'),
      id: 'updated_at',
      enableSorting: true,
      minWidth: '100px',
      maxWidth: '150px',
    }),
    columnHelper.tag((event) => event.rootcauses?.map((rootcause) => rootcause.name) || [], {
      header: t('common.labels.rootCause'),
      id: 'root_cause',
      enableSorting: false,
      minWidth: '100px',
      statuses: {},
    }),
    columnHelper.text((event) => event.email, {
      header: t('common.labels.ownerEmail'),
      id: 'owner_email',
      enableSorting: true,
      minWidth: '150px',
      maxWidth: '300px',
    }),
    columnHelper.text((event) => event.severity, {
      header: t('common.labels.severity'),
      id: 'severity',
      enableSorting: true,
      minWidth: '100px',
      maxWidth: '150px',
    }),
    columnHelper.text((event) => event.probability, {
      header: t('common.labels.probability'),
      id: 'probability',
      enableSorting: true,
      minWidth: '100px',
      maxWidth: '150px',
    }),
    ...(isCollaboratorsColumnEnabled
      ? [
          columnHelper.tag(
            (event) =>
              event.collaborators.map((collaborator) =>
                collaborator.is_active
                  ? collaborator.full_name
                  : t('common.labels.deactivatedUser', { name: collaborator.full_name }),
              ),
            {
              header: t('events.labels.collaborators'),
              id: 'collaborators',
              enableSorting: false,
              minWidth: '150px',
              statuses: {},
            },
          ),
        ]
      : []),
  ];

  return isConfigurableColumnsEnabled ? [...baseColumns, ...configurableColumns] : baseColumns;
};
